import React, { useEffect, useRef, useState } from 'react';
import s from './chatStyles';
import { timeAgo } from './format';
import { statusInfo } from './status';
import { Trash2, Bot, PenLine, Loader2, Clock } from 'lucide-react';

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

/**
 * Lista compacta de conversaciones (estilo WhatsApp / Teams): dos líneas por
 * fila. El estado va en el color del avatar; para el admin, las iniciales del
 * asesor asignado en la esquina; las etiquetas como puntos de color.
 * Al llegar al final carga la siguiente página.
 * En mis casos, "Espera 4m" si el cliente escribió y no se le ha respondido;
 * en rojo pasados `replyAlertMinutes`.
 */
export default function ConversationList({
  conversations, selectedId, unread, currentUserId, replyAlertMinutes, isAdmin, search, onSelect, onDelete,
  hasMore, loadingMore, onLoadMore,
}) {
  const sentinelRef = useRef(null);
  // Refresca cada 30 s los minutos de espera
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || !window.IntersectionObserver) return undefined;
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) onLoadMoreRef.current();
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, conversations.length]);

  return (
    <div style={s.convList} role="list">
      {conversations.length === 0 && (
        <div style={s.emptyList}>
          {search ? 'Sin resultados' : isAdmin ? 'Sin conversaciones' : 'No tienes conversaciones asignadas'}
        </div>
      )}
      {conversations.map(conv => {
        const st = statusInfo(conv.status);
        const isActive = selectedId === conv.session_id;
        const badge = unread[conv.session_id] || 0;
        const agent = isAdmin && conv.status === 'assigned' ? conv.assigned_user : null;
        // Espera del cliente: el asesor la ve en sus casos; el admin, en todos los que
        // debe responder una persona (asignados y en cola), para supervisar
        const watchesWait = isAdmin
          ? ['assigned', 'queued'].includes(conv.status)
          : conv.status === 'assigned' && conv.assigned_user?.id === currentUserId;
        const waiting = watchesWait && conv.waiting_since
          ? Date.now() - new Date(conv.waiting_since).getTime() : null;
        const late = waiting != null && waiting >= replyAlertMinutes * 60000;
        return (
          <div
            key={conv.session_id}
            role="listitem"
            tabIndex={0}
            className={isActive ? 'ui-reveal' : 'ui-row ui-reveal'}
            onClick={() => onSelect(conv)}
            onKeyDown={e => { if (e.key === 'Enter') onSelect(conv); }}
            style={{ ...s.convItem, ...(isActive ? s.convItemActive : {}) }}
            title={`${st.label}${agent?.name ? ` · ${agent.name}` : ''}`}
          >
            <div style={{ ...s.convAva, background: st.soft, color: st.color }}>
              <st.Icon size={17} strokeWidth={2} />
              {agent && <span style={s.avaAgent}>{initials(agent.name || '?')}</span>}
            </div>
            <div style={s.convBody}>
              <div style={s.convRow1}>
                <span style={s.convNameWrap}>
                  <span style={{ ...s.convName, ...(badge ? { fontWeight: 800 } : {}) }}>{conv.contact_name || conv.user_id}</span>
                  {conv.contact_name && <span style={s.convPhone}>{conv.user_id}</span>}
                </span>
                <span style={{ ...s.convTime, ...(badge ? { color: 'var(--accent)', fontWeight: 700 } : {}) }}>{timeAgo(conv.last_at)}</span>
              </div>
              <div style={s.convRow2}>
                <span style={{ ...s.convPrev, ...(badge ? { color: 'var(--text)', fontWeight: 600 } : {}) }}>
                  {conv.last_type === 'ai' && <Bot size={12} style={s.prevIcon} />}
                  {conv.last_type === 'asesor' && <PenLine size={12} style={s.prevIcon} />}
                  {conv.last_message || ''}
                </span>
                {(conv.tags || []).slice(0, 3).map(t => (
                  <span key={t.id} style={{ ...s.tagDot, background: t.color }} title={t.name} aria-label={`Etiqueta ${t.name}`} />
                ))}
                {waiting != null && (
                  <span style={{ ...s.waitChip, ...(late ? s.waitChipLate : {}) }}
                    title={`El cliente espera respuesta desde hace ${timeAgo(conv.waiting_since)}`}>
                    <Clock size={10} /> {timeAgo(conv.waiting_since) === 'ahora' ? 'ahora' : `Espera ${timeAgo(conv.waiting_since)}`}
                  </span>
                )}
                {badge > 0 && <span style={s.badge} aria-label={`${badge} sin leer`}>{badge}</span>}
                {isAdmin && (
                  <button
                    className="ui-reveal-target"
                    onClick={e => { e.stopPropagation(); onDelete(conv.session_id); }}
                    style={s.deleteConvBtn}
                    title="Borrar conversación"
                    aria-label="Borrar conversación"
                  ><Trash2 size={13} /></button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {hasMore && (
        <div ref={sentinelRef} style={s.loadMore}>
          {loadingMore
            ? <><Loader2 size={14} className="ui-spin" /> Cargando más…</>
            : <button className="ui-hover" style={s.loadMoreBtn} onClick={onLoadMore}>Cargar más</button>}
        </div>
      )}
    </div>
  );
}
